import React, { useState, useEffect } from "react";
import axios from "axios";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Avatar,
  Typography,
  Pagination,
  Box,
  Divider,
  Grid,
  Chip,
  Alert,
  ImageList,
  ImageListItem,
  Skeleton,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CakeIcon from "@mui/icons-material/Cake";
import WcIcon from "@mui/icons-material/Wc";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import LockIcon from "@mui/icons-material/Lock";

import API_BASE_URL, { BASE_URL } from "../config";

const UserProfileDialog = ({
  selectedUser,
  openProfileDialog,
  handleProfileClose,
}) => {
  const [photos, setPhotos] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [photosPerPage] = useState(6);

  const [selectedImage, setSelectedImage] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isPrivate] = useState(false);

  const theme = useTheme();

  const fullScreen = useMediaQuery(
    theme.breakpoints.down("md")
  );

  // ======================================================
  // IMAGE URL
  // ======================================================

  const getPhotoUrl = (path) => {
    if (!path) {
      return "/chaudharylife/uploads/default-profile.png";
    }

    if (path.startsWith("blob:")) {
      return path;
    }

    if (
      path.startsWith("http") &&
      !path.includes("localhost")
    ) {
      return path;
    }

    if (
      path.startsWith("http") &&
      path.includes("localhost")
    ) {
      return path.replace(
        /http:\/\/localhost:\d+/,
        BASE_URL
      );
    }

    if (path.startsWith("/")) {
      return `${BASE_URL}${path}`;
    }

    return `${BASE_URL}/uploads/${path}`;
  };

  // ======================================================
  // FETCH PHOTOS
  // ======================================================

  const fetchPhotos = async (userId) => {
    if (!userId) {
      setPhotos([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${API_BASE_URL}/photos`,
        {
          params: {
            userId,
          },
          withCredentials: true,
        }
      );

      const data = Array.isArray(response.data)
        ? response.data
        : [];

      setPhotos(data);
    } catch (error) {
      if (error.response?.status === 404) {
        setPhotos([]);
        return;
      }

      if (error.response?.status === 401) {
        setPhotos([]);
        setError("Please login to view photos.");
        return;
      }

      if (error.response?.status === 403) {

        setPhotos([]);
        setError("Unable to load photos.");
        return;
      }

      console.error(
        "Error fetching profile photos:",
        error
      );

      setPhotos([]);
      setError("Unable to load photos.");
    } finally {
      setLoading(false);
    }
  };

  // ======================================================
  // LOAD PROFILE PHOTOS
  // ======================================================

  useEffect(() => {
    if (!selectedUser || !openProfileDialog) {
      return;
    }

    setCurrentPage(1);
    setSelectedImage(null);

    fetchPhotos(selectedUser.id);
  }, [selectedUser, openProfileDialog]);

  // ======================================================
  // PAGINATION
  // ======================================================

  const indexOfLastPhoto =
    currentPage * photosPerPage;

  const indexOfFirstPhoto =
    indexOfLastPhoto - photosPerPage;

  const currentPhotos = photos.slice(
    indexOfFirstPhoto,
    indexOfLastPhoto
  );

  const totalPages = Math.ceil(
    photos.length / photosPerPage
  );

  // ======================================================
  // INFO ITEM
  // ======================================================

  const InfoItem = ({
    icon,
    label,
    value,
  }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        mb: 2,
        gap: 1,
      }}
    >
      {icon}

      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
        >
          {label}
        </Typography>

        <Typography variant="body1">
          {value || "Not specified"}
        </Typography>
      </Box>
    </Box>
  );

  // ======================================================
  // NO USER SELECTED
  // ======================================================

  if (!selectedUser) {
    return null;
  }

  // ======================================================
  // UI
  // ======================================================

  return (
    <>
      <Dialog
        open={openProfileDialog}
        onClose={handleProfileClose}
        fullWidth
        maxWidth="md"
        fullScreen={fullScreen}
      >
        {/* ================= HEADER ================= */}

        <DialogTitle
          sx={{
            px: 3,
            py: 2,

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",

            bgcolor: "primary.main",
            color: "white",

            fontSize: "1.25rem",
            fontWeight: 600,
          }}
        >
          <span>Profile Details</span>

          <IconButton
            aria-label="close"
            onClick={handleProfileClose}
            sx={{
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* ================= CONTENT ================= */}

        <DialogContent sx={{ p: 3 }}>
          {/* ================= PRIVATE PROFILE ================= */}

          {isPrivate ? (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
              }}
            >
              <LockIcon
                sx={{
                  fontSize: 64,
                  color: "text.disabled",
                  mb: 2,
                }}
              />

              <Typography
                variant="h6"
                color="text.secondary"
              >
                This profile is private.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* ================= PROFILE HEADER ================= */}

              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    mt: 1,
                  }}
                >
                  {selectedUser.image_url ? (
                    <Avatar
                      src={getPhotoUrl(
                        selectedUser.image_url
                      )}
                      alt={`${selectedUser.first_name || ""} ${
                        selectedUser.last_name || ""
                      }`}
                      sx={{
                        width: 120,
                        height: 120,
                      }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 120,
                        height: 120,
                        fontSize: "3rem",
                      }}
                    >
                      {selectedUser.first_name?.[0] ||
                        "?"}
                    </Avatar>
                  )}

                  <Box>
                    <Typography
                      variant="h5"
                      component="div"
                      gutterBottom
                    >
                      {selectedUser.first_name || ""}{" "}
                      {selectedUser.last_name || ""}
                    </Typography>

                    <Chip
                      label={
                        selectedUser.gender ||
                        "Not specified"
                      }
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </Grid>

              {/* ================= CONTACT ================= */}

              <Grid item xs={12} md={6}>
                <Typography
                  variant="h6"
                  gutterBottom
                >
                  Contact Information
                </Typography>

                <Box sx={{ ml: 1 }}>
                  <InfoItem
                    icon={
                      <EmailIcon color="primary" />
                    }
                    label="Email Address"
                    value={selectedUser.email_id}
                  />

                  <InfoItem
                    icon={
                      <PhoneIcon color="primary" />
                    }
                    label="Phone Number"
                    value={selectedUser.phone_number}
                  />

                  <InfoItem
                    icon={
                      <LocationOnIcon color="primary" />
                    }
                    label="Address"
                    value={[
                      selectedUser.area,
                      selectedUser.district,
                      selectedUser.state,
                      selectedUser.pincode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  />
                </Box>
              </Grid>

              {/* ================= PERSONAL ================= */}

              <Grid item xs={12} md={6}>
                <Typography
                  variant="h6"
                  gutterBottom
                >
                  Personal Information
                </Typography>

                <Box sx={{ ml: 1 }}>
                  <InfoItem
                    icon={
                      <CakeIcon color="primary" />
                    }
                    label="Age"
                    value={selectedUser.age}
                  />

                  <InfoItem
                    icon={
                      <WcIcon color="primary" />
                    }
                    label="Gender"
                    value={selectedUser.gender}
                  />
                </Box>
              </Grid>

              {/* ================= PHOTO GALLERY ================= */}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />

                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <PhotoLibraryIcon color="primary" />

                  Photo Gallery
                </Typography>

                {/* Loading */}

                {loading ? (
                  <ImageList
                    cols={3}
                    gap={16}
                  >
                    {[1, 2, 3].map((i) => (
                      <ImageListItem key={i}>
                        <Skeleton
                          variant="rectangular"
                          height={200}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                ) : error ? (
                  /* Error */

                  <Alert
                    severity="info"
                    sx={{ mt: 2 }}
                  >
                    {error}
                  </Alert>
                ) : photos.length === 0 ? (
                  /* Empty */

                  <Alert
                    severity="info"
                    sx={{ mt: 2 }}
                  >
                    No photos uploaded yet.
                  </Alert>
                ) : (
                  <>
                    {/* Photos */}

                    <ImageList
                      cols={3}
                      gap={16}
                    >
                      {currentPhotos.map(
                        (photo) => (
                          <ImageListItem
                            key={photo.id}
                            sx={{
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              setSelectedImage(
                                photo
                              )
                            }
                          >
                            <img
                              src={getPhotoUrl(
                                photo.filename
                              )}
                              alt="User Photo"
                              loading="lazy"
                              style={{
                                height: 200,
                                width: "100%",
                                objectFit:
                                  "cover",
                                borderRadius: 8,
                              }}
                            />
                          </ImageListItem>
                        )
                      )}
                    </ImageList>

                    {/* Pagination */}

                    {totalPages > 1 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent:
                            "center",
                          mt: 3,
                        }}
                      >
                        <Pagination
                          count={totalPages}
                          page={currentPage}
                          onChange={(_, value) =>
                            setCurrentPage(
                              value
                            )
                          }
                          color="primary"
                        />
                      </Box>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      {/* ======================================================
          FULL SIZE IMAGE
      ====================================================== */}

      <Dialog
        open={Boolean(selectedImage)}
        onClose={() => setSelectedImage(null)}
        maxWidth="xl"
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedImage && (
            <Box
              sx={{
                position: "relative",
              }}
            >
              <IconButton
                onClick={() =>
                  setSelectedImage(null)
                }
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  zIndex: 1,

                  bgcolor:
                    "rgba(0, 0, 0, 0.5)",

                  "&:hover": {
                    bgcolor:
                      "rgba(0, 0, 0, 0.7)",
                  },
                }}
              >
                <CloseIcon
                  sx={{
                    color: "white",
                  }}
                />
              </IconButton>

              <img
                src={getPhotoUrl(
                  selectedImage.filename
                )}
                alt="Full size"
                style={{
                  display: "block",
                  maxHeight: "90vh",
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserProfileDialog;